import React from 'react';
import { Calendar, FileSearch, FileCheck, Shield } from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';

const HowItWorks = () => {
  const { t, selectedCountry } = useCurrency();
  const isIndia = selectedCountry.code === 'IN';
  const steps = [
    {
      icon: Calendar,
      number: '01',
      title: 'Book in 60 Seconds',
      description: 'Schedule an inspection at your preferred time and location. Our expert will come to you.',
      highlight: 'Free cancellation',
    },
    {
      icon: FileSearch,
      number: '02',
      title: 'Expert Inspection',
      description: 'Our certified mechanic performs a comprehensive 200+ point AI-assisted inspection.',
      highlight: '2-3 hours',
    },
    {
      icon: FileCheck,
      number: '03',
      title: 'Get Digital Report',
      description: 'Receive a detailed report with AI rating, photos, and market value estimation.',
      highlight: 'Instant delivery',
    },
    {
      icon: Shield,
      number: '04',
      title: 'Buy with Protection',
      description: 'Get warranty coverage and financing options based on your car\'s inspection score.',
      highlight: 'Warranty eligible',
    },
  ];

  return (
    <section id="how-it-works" className="py-14 bg-slate-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">
            {t('howItWorks.subTitle')}

          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {t('howItWorks.title')}

          </h2>
          <p className="text-slate-600">
            {t('howItWorks.description')}

          </p>
        </div>

        {/* Steps - Compact */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-20 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-4">
            {t('howItWorks.steps')?.map((step, index) => (
              <div key={index} className="relative group">
                {/* Step Card */}
                <div className="bg-white rounded-xl p-5 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 h-full flex flex-col">
                  {/* Number Badge */}
                  <div className="relative z-10 mb-4">
                    <div className="h-12 w-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                      <step.icon className="h-6 w-6 text-white" />
                    </div>
                    <span className="absolute -top-1 -right-1 bg-slate-900 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {step.number}
                    </span>
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed flex-grow">
                    {step.description}
                  </p>

                  {/* Highlight Tag */}
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <span className="inline-flex items-center text-xs font-medium text-blue-600">
                      {step.highlight}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
